/**
 * Swiper 10.3.1
 * Most modern mobile touch slider and framework with hardware accelerated transitions
 * https://swiperjs.com
 *
 * Copyright 2014-2023 Vladimir Kharlampidi
 *
 * Released under the MIT License
 * 
 * License: See assets/LICENSE-swiper.txt
 * 
 * Released on: September 28, 2023
 */

const swiperOptions = [
    {
        name: "slideshow",
        options: {
            loop: false
        }
    },
    {
        name: "featured-collections",
        options: {
            loop: false
        }
    },
    {
        name: "countdown-timer",
        options: {
            loop: false
        }
    },
    {
        name: "featured-collection",
        options: {
            loop: false
        }
    },
    {
        name: "recently-viewed-products",
        options: {
            loop: false
        }
    },
    {
        name: "related-products",
        options: {
            loop: false
        }
    },
    {
        name: "complementory-products",
        options: {
            loop: false
        }
    },
    {
        name: "logo-list",
        options: {
            loop: false
        }
    },
    {
        name: "latest-blog-articles",
        options: {
            loop: false
        }
    },
    {
        name: "main-product-slider",
        options: {
            loop: false,
            allowTouchMove: false,
            on: {
                init: (swiper) => {
                    let mediaId;
                    const swiperElement = swiper.el;
                    const variation = swiperElement.getAttribute("data-variation");
                    const formId = swiperElement.getAttribute("data-form-id");
                    const isModal = swiperElement.hasAttribute("data-is-modal-gallery");
                    const form = document.forms[formId];

                    const updateSwiperParams = () => {
                        if (isModal === true) {
                            swiper.params.direction = "horizontal";
                            swiper.params.speed = 0;
                            swiper.allowTouchMove = false;
                        } else {
                            if (window.innerWidth >= 992) {
                                swiper.params.direction = "vertical";
                                swiper.params.speed = 300;
                                swiper.allowTouchMove = true;
                            } else {
                                swiper.params.direction = "horizontal";
                                swiper.params.speed = 0;
                                swiper.allowTouchMove = false;
                            }
                        }

                        swiper.update();
                    }

                    const handleOnClick = (event) => {
                        if (window.innerWidth >= 992) {
                            if (event.target.closest("[data-swiper-slide-index]:not(.active)")) {
                                const activeSlide = event.target.closest("[data-swiper-slide-index]");
                                const activeSlideIndex = parseInt(activeSlide.getAttribute("data-swiper-slide-index"));
                                swiper.slideTo(parseInt(activeSlide.getAttribute("data-swiper-slide-index")));
                                
                                swiper.slides.forEach(slide => {
                                    slide.classList.remove("active");
                                });
                                activeSlide.classList.add("active");
                                new SlideSelection(swiper.slides[activeSlideIndex]);
                                mediaId = activeSlide.getAttribute("data-media-id");
                                document.dispatchEvent(new CustomEvent("swiper-navigation:update"));
                            }
                        }
                    }

                    updateSwiperParams();

                    const onResizeUpdate = debounce(() => {
                        updateSwiperParams();
                    }, 400);

                    if (variation !== "slider") {
                        const enableOnResize = debounce(() => {
                            swiper.enabled = (window.innerWidth >= 992) ? false : true;
                        }, 300);

                        enableOnResize();

                        window.addEventListener("resize", enableOnResize)
                    }

                    new SliderNavigation(swiper);
                    new Media(swiperElement);

                    swiperElement.addEventListener("click", handleOnClick);
                    window.addEventListener("resize", onResizeUpdate);

                    if(!form) return;

                    form.addEventListener("variant:changed", (e) => {
                        if (e.detail.variant && e.detail.variant.featured_media) {
                            const imageId = e.detail.variant.featured_media.id;
                            const onPageMedia = swiper.el.closest(".shopify-section").querySelector("[data-selected-media-id]");
                            const onPageMediaId = onPageMedia.getAttribute("data-selected-media-id");
                            
                            if (+onPageMediaId === onPageMediaId) return;

                            const activeSlide = [...swiper.slides].find((slide) => {
                                const slideImageId = slide.getAttribute("data-media-id");
                                if (!slideImageId) return false;
                                return (parseInt(slideImageId) === imageId);
                            });
                            
                            if (activeSlide) {
                                const activeSlideIndex = parseInt(activeSlide.getAttribute("data-swiper-slide-index"));

                                if (window.innerWidth < 992 || ((window.innerWidth >= 992) && variation === "slider")) {
                                    swiper.slideTo(activeSlideIndex);
                                }
                                
                                swiper.slides.forEach(slide => {
                                    slide.classList.remove("active");
                                });

                                activeSlide.classList.add("active");

                                document.dispatchEvent(new CustomEvent("swiper-navigation:update"));

                                if (window.innerWidth >= 992) {
                                    if (imageId !== mediaId) {
                                        new SlideSelection(swiper.slides[activeSlideIndex]);
                                        mediaId = e.detail.variant.featured_media.id;
                                    }
                                }
                            }
                        }
                    });
                },
                slideChange: (swiper) => {
                    const swiperElement = swiper.el;
                    const bottomShadow = swiperElement.querySelector("[data-swiper-shadow-bottom]");

                    if (bottomShadow) {
                        if(swiper.isEnd) {
                            if (!bottomShadow.classList.contains("hide")) {
                                bottomShadow.classList.add("hide")
                            }
                        } else {
                            if (bottomShadow.classList.contains("hide")) {
                                bottomShadow.classList.remove("hide")
                            }
                        }
                    }

                    new Media(swiperElement).resetMedia();
                }
            }
        }
    },
    {
        name: "header-slider",
        options: {
            loop: false,
            slidesPerGroup: 3,
            on: {
                init: (swiper) => {
                    const sliderComponent = swiper.el.closest("slider-component");
                    const buttonNext = swiper.navigation.nextEl;
                    const buttonPrev = swiper.navigation.prevEl;

                    const handleOnFocus = (event) => {
                        const currentSlide = event.target.closest(".header-links__slider-item.swiper-slide");

                        if (!currentSlide) return;

                        const currentSlideIndex = currentSlide.getAttribute("data-slide-index");
                        swiper.slideTo(+currentSlideIndex);
                    };

                    const buttonsEnabler = (enable) => {
                        if (enable !== true) {
                            if (buttonNext.classList.contains("show")) {
                               buttonNext.classList.remove("show"); 
                            }
                            if (buttonPrev.classList.contains("show")) {
                                buttonPrev.classList.remove("show"); 
                            }
                        } else {
                            if (!buttonNext.classList.contains("show")) {
                                buttonNext.classList.add("show"); 
                            }
                             if (!buttonPrev.classList.contains("show")) {
                                 buttonPrev.classList.add("show"); 
                            }
                        }
                    }

                    const sliderEnabler = () => {
                        sliderComponent.classList.remove("header-links--slider")

                        if (window.innerWidth >= 992) {
                            swiper.wrapperEl.classList.remove("transform-none");

                            const enable = !swiper.allowSlideNext || !swiper.allowSlidePrev;
                            swiper.enable();
                            buttonsEnabler(!enable);

                            if (!enable && !sliderComponent.classList.contains("header-links--slider")) {
                                sliderComponent.classList.add("header-links--slider")
                            }

                            swiper.slides.forEach(slide => {
                                slide.addEventListener("focusin", handleOnFocus)
                            });
                        } else {
                            swiper.disable();
                            swiper.wrapperEl.classList.add("transform-none");
                            
                            buttonsEnabler(false);

                            swiper.slides.forEach(slide => {
                                slide.removeEventListener("focusin", handleOnFocus)
                            });
                        }

                        swiper.update();
                    }

                    sliderEnabler();

                    const debouncedOnResize = debounce(() => {
                        sliderEnabler();
                    }, 300);

                    window.addEventListener("resize", debouncedOnResize);

                    if (Shopify.designMode) {
                        document.addEventListener("shopify:section:load", debouncedOnResize);
                    }
                }
            }
        }
    },
    {
        name: "best-seller-slider",
        options: {
            loop: false,
            speed: 300,
            allowTouchMove: false,
            effect: "fade",
            on: {
                init: (swiper) => {
                    const template = swiper.slides[swiper.activeIndex].querySelector("[data-template-content]");
                    new UpdateInfo(template);
                },
                
                slideChange: (swiper) => {
                    const debouncedOnChange = debounce(() => {
                        const template = swiper.slides[swiper.activeIndex].querySelector("[data-template-content]");
                        new UpdateInfo(template);
                    }, 300)
                    
                    debouncedOnChange();
                }
            }
        }
    },
    {
        name: "scroll-and-fix-slider",
        options: {
            loop: false,
            breakpoints: {
                992: {
                    direction: "vertical"
                }
            },
            on: {
                init: (swiper) => {
                    const onWindowResize = () => {
                        if (window.innerWidth >= 992) {
                            swiper.disable();
                        } else {
                            swiper.enable();
                        }
                    }

                    onWindowResize();
                    window.addEventListener("resize", onWindowResize);
                }
            }
        }
    },
    {
        name: "inline-card-carousel",
        options: {
            loop: false
        }
    },
    {
        name: "dual-image-slider",
        options: {
            loop: false,
            direction: "horizontal",
            breakpoints: {
                992: {
                    direction: "vertical"
                }
            },
            on: {
                init: (swiper) => {
                    const calculateHeight = () => {
                        if (window.innerWidth >= 992) {
                            const maxHeight = Math.max(...swiper.slides.map(slide => slide.querySelector(".product-card").offsetHeight));
                            swiper.el.style.cssText = `max-height: ${maxHeight}px;`
                        } else {
                            swiper.el.style.cssText = ``;
                        }
                    }
                    
                    calculateHeight();

                    if (Shopify.designMode) {
                        document.addEventListener("shopify:section:load", (e) => {
                            if (e.target.querySelector(".dual-image-slider")) {
                                setTimeout(() => {
                                    calculateHeight();
                                }, 0);
                            }
                        });
                    }
                    const debouncedOnResize = debounce(calculateHeight, 100)
                    window.addEventListener("resize", debouncedOnResize);
                },
                slideChangeTransitionEnd: (swiper) => {
                    const container = swiper.el.closest("[data-dual-image-container]");
                    if (!container) return
                    const activeSlide = swiper.slides[swiper.activeIndex];
                    const activeSlideId = activeSlide.dataset.id;
                    const activeImageBlock = container.querySelector(`[data-block-id="${activeSlideId}"]`);
                    const previousActiveImageBlock = container.querySelector("[data-block-id].active");

                    previousActiveImageBlock.classList.add("animate-off");

                    previousActiveImageBlock.onanimationend = (() => {
                        previousActiveImageBlock.classList.remove("active");
                        previousActiveImageBlock.classList.remove("animate-off");
                        activeImageBlock.classList.add("active");
                    });
                }
            }
        }
    },
    {
        name: "testimonials-slider",
        options: {
            loop: false
        }
    }
]
